import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { leerErrorDeEdgeFunction } from "@/lib/edge-function-error";

/**
 * Instancia de Evolution API por la que sale el WhatsApp (edge `admin-whatsapp-config`,
 * tabla `config_whatsapp_evolution`). La apikey nunca llega al front: solo enmascarada.
 */

export const RUTA_CONFIG_WHATSAPP = "/admin/configuracion-whatsapp";

/** Permiso del submenú WhatsApp que muestra el monitor del header y deja reconectar con QR. */
export const PERMISO_CONECTAR_TELEFONO = "conectar_telefono_notificaciones";

export type EstadoWhatsapp =
  | "sin_revisar"
  | "sin_configurar"
  | "conectado"
  | "conectando"
  | "desconectado"
  | "error";

export type FuenteWhatsapp = "bd" | "secreto" | "ninguna";

export interface EstadoConexionWhatsapp {
  estado: EstadoWhatsapp;
  detalle: string | null;
  desde: string | null;
  ultima_conexion: string | null;
  revisado_at: string | null;
  instancia: string | null;
}

export interface ConfigWhatsapp {
  url_base: string | null;
  nombre_instancia: string | null;
  api_key_mascara: string | null;
  vigente: {
    url_base: string | null;
    nombre_instancia: string | null;
    api_key_mascara: string | null;
    fuente: { urlBase: FuenteWhatsapp; instancia: FuenteWhatsapp; apiKey: FuenteWhatsapp };
  };
  actualizado_at: string | null;
  actualizado_por: string | null;
}

export interface BorradorWhatsapp {
  url_base: string;
  nombre_instancia: string;
  /** Vacía = conservar la apikey guardada. */
  api_key: string;
  quitar_api_key?: boolean;
}

const MENSAJES: Record<string, string> = {
  sin_permiso: "No tienes permiso para esta configuración.",
  url_invalida: "La URL de Evolution no es válida (debe empezar con https://).",
  campo_invalido: "Algún campo es demasiado largo.",
  config_no_disponible: "La tabla de configuración aún no existe en este ambiente.",
};

export async function llamarWhatsappConfig<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-whatsapp-config", { body });
  if (error) {
    const codigo = await leerErrorDeEdgeFunction(error);
    throw new Error(MENSAJES[codigo] ?? (codigo || "No se pudo consultar la configuración de WhatsApp."));
  }
  return data as T;
}

/** Estado de la conexión, refrescado cada minuto. La edge reusa revisiones de < 60 s. */
export function useEstadoWhatsapp(enabled: boolean) {
  return useQuery({
    queryKey: ["whatsapp-evolution-estado"],
    queryFn: async () =>
      (await llamarWhatsappConfig<{ estado: EstadoConexionWhatsapp }>({ action: "estado" })).estado,
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    retry: 1,
    staleTime: 30_000,
  });
}

export type TonoWhatsapp = "verde" | "ambar" | "rojo" | "gris";

export const horaCorta = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    : "—";

export function tonoWhatsapp(s: EstadoConexionWhatsapp | undefined, fallo: boolean): TonoWhatsapp {
  // Todo lo que no sea una sesión abierta es rojo: "conectando" es también como se
  // queda la instancia tras pedir el QR sin escanearlo, y mientras tanto no sale nada.
  if (!fallo && s?.estado === "conectado") return "verde";
  return "rojo";
}
